import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Car, Armchair, House, Buildings, MapPin, Clock, Shield, Star } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import ReviewsSection from "@/components/ReviewsSection";
import RealisationsSection from "@/components/RealisationsSection";

const TARIFS_AUTO = [
  { cat: "Citadine", cdp: "50€", approf: "80€", ext: "40€", classique: "90€", deluxe: "140€" },
  { cat: "Berline", cdp: "60€", approf: "90€", ext: "50€", classique: "110€", deluxe: "160€" },
  { cat: "SUV (5 pl)", cdp: "70€", approf: "100€", ext: "60€", classique: "130€", deluxe: "180€" },
  { cat: "Van (8-9 pl)", cdp: "90€", approf: "110€", ext: "70€", classique: "160€", deluxe: "200€" },
];

const SEO_PAGES = {
  "nettoyage-auto-ile-de-france": {
    title: "Nettoyage Automobile à Domicile — Île-de-France",
    h1: "Nettoyage automobile à domicile en Île-de-France",
    description: "Service professionnel de nettoyage intérieur et extérieur de votre véhicule, directement chez vous. Citadine, berline, SUV — tarification transparente.",
    icon: Car,
    service: "auto",
    cta_link: "/property-services/automobile",
    cta_text: "Réserver un nettoyage auto",
    zones: ["Versailles", "Poissy", "Saint-Germain-en-Laye", "Le Chesnay", "Orgeval", "Le Vésinet", "Chambourcy", "Mantes-la-Jolie", "Les Mureaux", "Conflans"],
    localPages: [
      { to: "/property-services/nettoyage-auto-versailles", label: "Nettoyage auto à Versailles" },
      { to: "/property-services/nettoyage-auto-poissy", label: "Nettoyage auto à Poissy" },
      { to: "/property-services/nettoyage-auto-saint-germain-en-laye", label: "Nettoyage auto à Saint-Germain-en-Laye" },
    ],
    features: [
      { title: "À domicile", desc: "Nous venons directement à votre adresse, parking ou résidence." },
      { title: "5 formules", desc: "Coup de propre, Intérieur approfondi, Extérieur, Classique ou Deluxe." },
      { title: "Tarifs fixes", desc: "Prix affichés selon la catégorie de votre véhicule, pas de surprise." },
      { title: "Produits pro", desc: "Shampooing, vapeur, microfibre — matériel professionnel à chaque intervention." },
    ],
    tarifs: TARIFS_AUTO,
  },
  "nettoyage-auto-versailles": {
    title: "Nettoyage Auto à Domicile à Versailles — ALTURA",
    h1: "Nettoyage automobile à domicile à Versailles",
    description: "Nettoyage intérieur et extérieur de votre véhicule à Versailles, Le Chesnay, Bailly, Noisy-le-Roi et Saint-Nom-la-Bretèche. Intervention à domicile, tarifs transparents.",
    icon: Car,
    service: "auto",
    city: "Versailles",
    cta_link: "/property-services/automobile",
    cta_text: "Réserver un nettoyage auto",
    whatsapp: "Bonjour Altura, je souhaite un nettoyage de véhicule à Versailles. Pouvez-vous me confirmer le tarif et les disponibilités ?",
    zones: ["Versailles", "Le Chesnay-Rocquencourt", "Bailly", "Noisy-le-Roi", "Saint-Nom-la-Bretèche", "Buc", "Viroflay"],
    features: [
      { title: "À domicile à Versailles", desc: "Nos équipes se déplacent à votre adresse, parking ou résidence dans le secteur de Versailles." },
      { title: "5 formules au choix", desc: "Du coup de propre rapide à la formule Deluxe complète." },
      { title: "Tarifs fixes", desc: "Prix identiques quelle que soit votre commune dans le secteur." },
      { title: "Base Poissy", desc: "Déplacement inclus selon la distance. Envoyez votre adresse pour vérifier." },
    ],
    tarifs: TARIFS_AUTO,
  },
  "nettoyage-auto-poissy": {
    title: "Nettoyage Auto à Domicile à Poissy — ALTURA",
    h1: "Nettoyage automobile à domicile à Poissy",
    description: "Nettoyage professionnel de votre véhicule à Poissy, Orgeval, Villennes-sur-Seine, Médan et Chambourcy. Intervention à domicile, devis immédiat.",
    icon: Car,
    service: "auto",
    city: "Poissy",
    cta_link: "/property-services/automobile",
    cta_text: "Réserver un nettoyage auto",
    whatsapp: "Bonjour Altura, je souhaite un nettoyage de véhicule à Poissy. Pouvez-vous me confirmer le tarif et les disponibilités ?",
    zones: ["Poissy", "Orgeval", "Villennes-sur-Seine", "Médan", "Chambourcy", "Aigremont", "Morainvilliers"],
    features: [
      { title: "Proche de notre base", desc: "Poissy est notre base — pas de frais de déplacement." },
      { title: "5 formules au choix", desc: "Coup de propre, Intérieur approfondi, Extérieur, Classique ou Deluxe." },
      { title: "Options à la carte", desc: "Shampoing sièges, soin cuir, retrait poils — personnalisez votre intervention." },
      { title: "Intervention rapide", desc: "Créneaux disponibles sous 48h dans le secteur de Poissy." },
    ],
    tarifs: TARIFS_AUTO,
  },
  "nettoyage-auto-saint-germain-en-laye": {
    title: "Nettoyage Auto à Domicile à Saint-Germain-en-Laye — ALTURA",
    h1: "Nettoyage automobile à domicile à Saint-Germain-en-Laye",
    description: "Nettoyage auto professionnel à Saint-Germain-en-Laye, Le Vésinet, Maisons-Laffitte, Chatou, Croissy-sur-Seine, Marly-le-Roi et Louveciennes.",
    icon: Car,
    service: "auto",
    city: "Saint-Germain-en-Laye",
    cta_link: "/property-services/automobile",
    cta_text: "Réserver un nettoyage auto",
    whatsapp: "Bonjour Altura, je souhaite un nettoyage de véhicule à Saint-Germain-en-Laye. Pouvez-vous me confirmer le tarif et les disponibilités ?",
    zones: ["Saint-Germain-en-Laye", "Le Vésinet", "Maisons-Laffitte", "Croissy-sur-Seine", "Chatou", "Marly-le-Roi", "Louveciennes"],
    features: [
      { title: "Secteur résidentiel", desc: "Intervention à domicile dans toutes les communes résidentielles du secteur." },
      { title: "5 formules", desc: "Du simple coup de propre à la formule Deluxe, selon vos besoins." },
      { title: "Tarifs transparents", desc: "Les conditions de déplacement dépendent de votre adresse exacte. Vérifiez en un clic." },
      { title: "Matériel professionnel", desc: "Shampooing, vapeur, microfibre — tout est fourni par nos équipes." },
    ],
    tarifs: TARIFS_AUTO,
  },
  "nettoyage-canape-tapis-ile-de-france": {
    title: "Nettoyage Canapé & Tapis à Domicile — Île-de-France",
    h1: "Nettoyage canapé, tapis et matelas en Île-de-France",
    description: "Nettoyage professionnel de votre mobilier à domicile. Canapés, tapis, matelas — résultat garanti avec des techniques professionnelles.",
    icon: Armchair,
    service: "mobilier",
    cta_link: "/property-services/mobilier",
    cta_text: "Demander un devis mobilier",
    whatsapp: "Bonjour Altura, je souhaite un nettoyage de mobilier. Pouvez-vous me confirmer le tarif et les disponibilités ?",
    zones: ["Versailles", "Poissy", "Saint-Germain-en-Laye", "Le Chesnay", "Orgeval", "Le Vésinet", "Maisons-Laffitte", "Mantes-la-Jolie"],
    features: [
      { title: "À domicile", desc: "Intervention chez vous, sans déplacer votre mobilier." },
      { title: "Shampooing pro", desc: "Injection-extraction pour un nettoyage en profondeur." },
      { title: "Anti-acariens", desc: "Traitement anti-acariens et désodorisant inclus." },
      { title: "Séchage rapide", desc: "Votre mobilier est utilisable en quelques heures." },
    ],
    tarifs: null,
  },
  "menage-appartement-ile-de-france": {
    title: "Ménage Appartement Professionnel — Île-de-France",
    h1: "Ménage d'appartement professionnel en Île-de-France",
    description: "Service de ménage à l'heure pour votre appartement ou maison. Matériel fourni ou non, minimum 2 heures. Idéal aussi entre deux locations Airbnb.",
    icon: House,
    service: "appartement",
    cta_link: "/property-services/appartement",
    cta_text: "Réserver un ménage",
    whatsapp: "Bonjour Altura, je souhaite un ménage d'appartement. Pouvez-vous me confirmer le tarif et les disponibilités ?",
    zones: ["Versailles", "Poissy", "Saint-Germain-en-Laye", "Le Chesnay", "Orgeval", "Le Vésinet", "Mantes-la-Jolie"],
    features: [
      { title: "35€ ou 40€/h", desc: "Avec votre matériel ou le nôtre — vous choisissez." },
      { title: "Minimum 2h", desc: "Pour une intervention efficace et complète." },
      { title: "Maison, appartement, Airbnb", desc: "Ménage régulier, ponctuel ou entre deux locations." },
      { title: "Rayon 20 km inclus", desc: "Pas de frais de déplacement dans un rayon de 20 km autour de Poissy." },
    ],
    tarifs: null,
  },
  "nettoyage-locaux-professionnels-ile-de-france": {
    title: "Nettoyage Locaux Professionnels — Île-de-France",
    h1: "Nettoyage de locaux professionnels en Île-de-France",
    description: "Entretien de bureaux, commerces et espaces professionnels à Versailles, Poissy, Saint-Germain-en-Laye et dans les Yvelines. Devis personnalisé.",
    icon: Buildings,
    service: "commercial",
    cta_link: "/property-services/commercial",
    cta_text: "Demander un devis",
    whatsapp: "Bonjour Altura, je souhaite un devis pour le nettoyage de nos locaux professionnels. Pouvez-vous me contacter ?",
    zones: ["Versailles", "Poissy", "Saint-Germain-en-Laye", "La Défense", "Le Chesnay", "Orgeval", "Le Vésinet"],
    features: [
      { title: "Sur devis", desc: "Tarification adaptée à la surface et à la fréquence." },
      { title: "Tous locaux", desc: "Bureaux, commerces, restaurants, entrepôts." },
      { title: "Flexible", desc: "Intervention en journée, soirée ou week-end." },
      { title: "Contrat ou ponctuel", desc: "Engagement régulier ou intervention unique." },
    ],
    tarifs: null,
  },
};

export default function PropertySEOPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const page = SEO_PAGES[slug];

  if (!page) {
    return (
      <div className="min-h-[60vh] bg-[#EDEBE4] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-[#1A1A1A] mb-4">Page non trouvée</h1>
          <Link to="/property-services" className="text-[#1A1A1A]/60 text-sm underline">Retour aux Property Services</Link>
        </div>
      </div>
    );
  }

  const Icon = page.icon;

  return (
    <>
      <SEO title={`${page.title} — ALTURA Property Services`} description={page.description} />

      {/* Hero */}
      <section className="bg-[#1A1A1A] py-20 sm:py-28" data-testid={`seo-hero-${slug}`}>
        <div className="max-w-4xl mx-auto px-6">
          <button onClick={() => navigate("/property-services")} className="text-white/40 text-xs flex items-center gap-2 mb-8 hover:text-white/70">
            <ArrowLeft size={12} /> Property Services
          </button>
          <Icon size={32} weight="light" className="text-white/50 mb-4" />
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="font-heading text-3xl sm:text-4xl text-white tracking-wide mb-5">
            {page.h1}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            className="text-white/50 text-sm sm:text-base max-w-2xl leading-relaxed">
            {page.description}
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="flex flex-wrap gap-3 mt-8">
            <Link to={page.cta_link}
              className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] px-7 py-3 text-xs tracking-[0.15em] uppercase hover:bg-[#EDEBE4] transition-colors"
              data-testid="seo-cta">
              {page.cta_text} <ArrowRight size={14} weight="bold" />
            </Link>
            {page.whatsapp && (
              <a href={`https://wa.me/33681969342?text=${encodeURIComponent(page.whatsapp)}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-3 text-xs tracking-[0.15em] uppercase hover:border-white/50 transition-colors"
                data-testid="seo-whatsapp">
                Vérifier l'intervention à mon adresse
              </a>
            )}
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-[#EDEBE4] py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading text-xl sm:text-2xl text-[#1A1A1A] mb-10 text-center">Pourquoi choisir ALTURA ?</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {page.features.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-white border border-[#1A1A1A]/10 p-6">
                <h3 className="text-[#1A1A1A] text-sm font-medium tracking-wide uppercase mb-2">{f.title}</h3>
                <p className="text-[#1A1A1A]/60 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tariff table for auto */}
      {page.tarifs && (
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="font-heading text-xl sm:text-2xl text-[#1A1A1A] mb-8 text-center">Nos tarifs</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-[#1A1A1A]/10">
                    <th className="py-3 text-left text-[#1A1A1A]/50 text-xs uppercase tracking-wide">Catégorie</th>
                    <th className="py-3 text-center text-[#1A1A1A]/50 text-xs uppercase tracking-wide">Coup de propre</th>
                    <th className="py-3 text-center text-[#1A1A1A]/50 text-xs uppercase tracking-wide">Int. Approfondi</th>
                    <th className="py-3 text-center text-[#1A1A1A]/50 text-xs uppercase tracking-wide">Extérieur</th>
                    <th className="py-3 text-center text-[#1A1A1A]/50 text-xs uppercase tracking-wide">Classique</th>
                    <th className="py-3 text-center text-[#1A1A1A]/50 text-xs uppercase tracking-wide">Deluxe</th>
                  </tr>
                </thead>
                <tbody>
                  {page.tarifs.map(t => (
                    <tr key={t.cat} className="border-b border-[#1A1A1A]/5">
                      <td className="py-3 text-[#1A1A1A] font-medium">{t.cat}</td>
                      <td className="py-3 text-center text-[#1A1A1A]">{t.cdp}</td>
                      <td className="py-3 text-center text-[#1A1A1A]">{t.approf}</td>
                      <td className="py-3 text-center text-[#1A1A1A]">{t.ext}</td>
                      <td className="py-3 text-center text-[#1A1A1A]">{t.classique}</td>
                      <td className="py-3 text-center text-[#1A1A1A] font-medium">{t.deluxe}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[#1A1A1A]/40 text-xs mt-4 text-center">Camions et utilitaires : sur devis. Véhicule très sale / poils d'animaux : supplément sur devis.</p>
          </div>
        </section>
      )}

      {/* Zones */}
      <section className={`py-16 sm:py-20 ${page.tarifs ? "bg-[#EDEBE4]" : "bg-white"}`}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-xl sm:text-2xl text-[#1A1A1A] mb-3">Zones d'intervention</h2>
          <p className="text-[#1A1A1A]/50 text-sm mb-8">Nous intervenons dans toute l'Île-de-France, avec notre base à Poissy (78).</p>
          <div className="flex flex-wrap justify-center gap-2">
            {page.zones.map(z => (
              <span key={z} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#1A1A1A]/10 text-[#1A1A1A]/70 text-xs">
                <MapPin size={10} weight="fill" className="text-[#1A1A1A]/30" /> {z}
              </span>
            ))}
          </div>
          <p className="text-[#1A1A1A]/40 text-xs mt-4 text-center">Base ALTURA : Poissy (78300). Le déplacement est inclus dans un rayon de 20 km. Au-delà, envoyez votre adresse pour vérifier les conditions.</p>
          {page.whatsapp && (
            <div className="mt-4 text-center">
              <a href={`https://wa.me/33681969342?text=${encodeURIComponent(page.whatsapp)}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-6 py-2.5 text-xs tracking-[0.15em] uppercase hover:bg-[#333] transition-colors"
                data-testid="zone-whatsapp">
                Vérifier l'intervention à mon adresse
              </a>
            </div>
          )}
          {/* Links to local pages */}
          {page.localPages && page.localPages.length > 0 && (
            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              {page.localPages.map(l => (
                <Link key={l.to} to={l.to} className="flex items-center gap-2 bg-white border border-[#1A1A1A]/10 px-4 py-3 text-xs text-[#1A1A1A] hover:border-[#1A1A1A]/30 transition-colors group">
                  <span className="flex-1">{l.label}</span>
                  <ArrowRight size={12} className="text-[#1A1A1A]/20 group-hover:text-[#1A1A1A]" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <section className="bg-[#1A1A1A] py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            {[
              { icon: Shield, label: "Professionnels vérifiés", desc: "Équipe formée et assurée" },
              { icon: Clock, label: "Intervention rapide", desc: "Créneaux sous 48h" },
              { icon: Star, label: "Satisfaction garantie", desc: "Reprise gratuite si insatisfait" },
            ].map(s => (
              <div key={s.label}>
                <s.icon size={24} weight="light" className="text-white/40 mx-auto mb-2" />
                <p className="text-white text-sm font-medium mb-1">{s.label}</p>
                <p className="text-white/40 text-xs">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Reviews */}
      <RealisationsSection serviceType={page.service} city={page.city} title="Nos dernières réalisations" />
      <ReviewsSection serviceType={page.service} />

      {/* Final CTA */}
      <section className="bg-[#EDEBE4] py-12">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Link to={page.cta_link}
            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-8 py-3 text-xs tracking-[0.15em] uppercase hover:bg-[#333] transition-colors">
            {page.cta_text} <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </section>
    </>
  );
}
